import type { ReactNode } from "react";
import type { Citation } from "./citation";
import { booleanType } from "./types/boolean";
import { currencyType } from "./types/currency";
import { dateType } from "./types/date";
import { documentType } from "./types/document";
import { enumType } from "./types/enum";
import { multiType } from "./types/multi";
import { numberType } from "./types/number";
import { textType } from "./types/text";

/**
 * A field-type entry describes everything needed to format, render, and edit
 * values of a single logical type. The registry is keyed by type name
 * (e.g. "text", "number", "currency"). Consumers (grid cells, field-value
 * surfaces) should resolve entries via `getType(name)` rather than
 * importing types directly.
 *
 * `TValue` is intentionally unconstrained — currency, multi-value, and
 * future types use object/array shapes and must not be forced through
 * a `string | number` channel.
 */
export interface ValueTypeEntry<TValue, TOptions = unknown> {
  /** Stable type name. Must match the key used to register the entry. */
  name: string;
  /** Plain-text formatter for previews, tooltips, and copy-to-clipboard. */
  format: (value: TValue, options?: TOptions) => string;
  /** React renderer for read-only display. */
  render: (props: { value: TValue; options?: TOptions; citations?: Citation[] }) => ReactNode;
  /**
   * Cell-level read-only renderer. Where `render` returns the raw value
   * presentation (a string, a badge, an icon), `display` returns the
   * cell-ready wrapper (truncation, container behavior, options-driven
   * adapters). Consumers like `editable-cell.tsx` and `field-value.tsx`
   * call `display` rather than reimplementing the per-type dispatch.
   *
   * `value` is intentionally `unknown` so the cross-type call site in the
   * grid can pass `CellValue` without per-type narrowing — each entry
   * narrows internally.
   */
  display: (props: { value: unknown; options?: TOptions; citations?: Citation[] }) => ReactNode;
  /**
   * Cell-level wrapping hint. When `"truncate"`, the cross-type display
   * site (`value-display.tsx`) wraps the rendered node in a single-line
   * truncating container with hover overflow. Defaults to `"none"`.
   * Lives on the registry entry so the dispatch is data-driven rather
   * than stringly typed in the consumer.
   */
  wrap?: "truncate" | "none";
  /** React renderer for inline / popover edit. */
  edit: (props: {
    value: TValue;
    options?: TOptions;
    onCommit: (next: TValue) => void;
    onCancel: () => void;
  }) => ReactNode;
}

const registry = new Map<string, ValueTypeEntry<unknown, unknown>>();

/**
 * Register (or replace) a field-type entry. Later registrations overwrite
 * earlier ones for the same `name` — this is intentional for tests and
 * for app-level overrides.
 */
export function registerType<TValue, TOptions = unknown>(
  name: string,
  entry: ValueTypeEntry<TValue, TOptions>,
): void {
  registry.set(name, entry as ValueTypeEntry<unknown, unknown>);
}

/**
 * Resolve a field-type entry by name. Unknown types fall back to the
 * built-in `text` entry so callers always get a renderable shape.
 */
export function getType(name: string): ValueTypeEntry<unknown, unknown> {
  const entry = registry.get(name);
  if (entry) return entry;
  const fallback = registry.get("text");
  if (!fallback) {
    throw new Error("@quovy/field-types: text type is missing from the registry");
  }
  return fallback;
}

// Self-register built-in types on module load.
registerType("text", textType);
registerType("number", numberType);
registerType("boolean", booleanType);
registerType("date", dateType);
// `datetime` is a thin alias for `date` — the editor and display behavior
// are identical (UTC parsing, ISO-8601 wire shape). Registering it directly
// lets call sites drop the legacy `valueType === "datetime" ? "date" : ...`
// collapse.
registerType("datetime", dateType);
// `string` is the legacy synonym for `text` (TableValueType still carries
// both). Aliasing here lets call sites resolve via `getType(field.valueType)`
// without a per-call string remap.
registerType("string", textType);
registerType("currency", currencyType);
registerType("enum", enumType);
// CRITICAL: key is "multi-value" (with hyphen) — matches the canonical
// `TableValueType` string in `packages/types/src/tabular-editor.ts`. Registering
// under "multi" would silently fall back to text.
registerType("multi-value", multiType);
registerType("document", documentType);

export type { Citation } from "./citation";
export { parseCitation, parseCitations } from "./citation";
export { textType } from "./types/text";
export { numberType } from "./types/number";
export { booleanType } from "./types/boolean";
export { dateType } from "./types/date";
export {
  currencyType,
  formatCurrencyMajorValue,
  resolveCurrencyMinorDigits,
} from "./types/currency";
export { enumType } from "./types/enum";
export { multiType } from "./types/multi";
export { documentType } from "./types/document";
export type { DocumentOptions } from "./types/document";
export { displayFileNameFromObjectKey, FILE_KEY_PREFIX_RE } from "./lib/file-name";
